import React, {useState, useEffect, useRef} from "react";
import {Link} from "react-router-dom";
import {Motion, ChevronDown} from "lucide-react";
import logo from '../../Assets/Images/logo-black.webp';
import logowhite from '../../Assets/Images/logo-white.webp'
import './Navbar.css'
import Fixedbar from '../fixedbar/fixedbar';

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isServicesOpen, setIsServicesOpen] = useState(false);
    const [isFormationsOpen, setIsFormationsOpen] = useState(false);

    const menuRef = useRef(null);
    const servicesRef = useRef(null);
    const formationsRef = useRef(null);

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 100); // adjust threshold as needed
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setIsOpen(false);
        }
        if (servicesRef.current && !servicesRef.current.contains(event.target)) {
            setIsServicesOpen(false);
        }
        if (formationsRef.current && !formationsRef.current.contains(event.target)) {
            setIsFormationsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const toggleServices = (e) => {
        e.stopPropagation();
        setIsServicesOpen(!isServicesOpen);
        if (isFormationsOpen) setIsFormationsOpen(false);
    };
    const toggleFormations = (e) => {
        e.stopPropagation();
        setIsFormationsOpen(!isFormationsOpen);
    };

    const handleLinkClick = () => {
        setIsOpen(false);
        setIsServicesOpen(false);
        setIsFormationsOpen(false);
    };

    return (
        <nav
            className={`fixed top-0 w-[100vw] z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
            <Fixedbar isScrolled={isScrolled}/>
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-around items-center h-20">
                    {/* Logo */}
                    {isScrolled ? (<Link to="/" className="flex-shrink-0" onClick={handleLinkClick}>
                        <img
                            src={logo}
                            alt="Logo"
                            className="h-48 w-auto transition-all duration-300"
                        />
                    </Link>) : (<Link to="/" className="flex-shrink-0" onClick={handleLinkClick}>
                        <img
                            src={logowhite}
                            alt="Logo"
                            className="h-48 w-auto transition-all duration-300"
                        />
                    </Link>)}

                    {/* Desktop Menu */}
                    <div className="hidden lg:flex items-center space-x-8">
                        <Link to="/"
                              className={`nav-link ${isScrolled ? 'text-gray-800' : 'text-white'}`}
                              onClick={handleLinkClick}>
                            Home
                        </Link>
                        <Link to="/pricing"
                              className={`nav-link ${isScrolled ? 'text-gray-800' : 'text-white'}`}
                              onClick={handleLinkClick}>
                            Pricing
                        </Link>
                        <Link to="/zones-info-dubai"
                              className={`nav-link ${isScrolled ? 'text-gray-800' : 'text-white'}`}
                              onClick={handleLinkClick}>
                            Freezones
                        </Link>
                        <Link to="/services"
                              className={`nav-link ${isScrolled ? 'text-gray-800' : 'text-white'}`}
                              onClick={handleLinkClick}>
                            Services
                        </Link>
                        <Link to="/dubai-work-zones"
                              className={`nav-link ${isScrolled ? 'text-gray-800' : 'text-white'}`}
                              onClick={handleLinkClick}>
                            Companies
                        </Link>
                    </div>

                    {/* Mobile menu button */}
                    <button
                        onClick={toggleMenu}
                        className="lg:hidden inline-flex items-center justify-center p-1 focus:ring-inset focus:ring-purple-500"
                    >
                        <span className="sr-only">Open main menu</span>
                        <div className="relative w-14 h-18">
                            <span
                                className={`absolute w-6 h-0.5 bg-current transform transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'}`}
                            />
                            <span
                                className={`absolute w-6 h-0.5 bg-current transform transition-all duration-300 ${isOpen ? 'opacity-0' : 'opacity-100'}`}
                            />
                            <span
                                className={`absolute w-6 h-0.5 bg-current transform transition-all duration-300 ${isOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'}`}
                            />
                        </div>
                    </button>
                </div>

                {/* Mobile menu */}
                <div
                    className={`lg:hidden bg-white transition-all duration-300 ease-in-out ${isOpen ? 'max-h-screen opacity-100 px-4' : 'max-h-0 opacity-0 overflow-hidden'}`}
                    ref={menuRef}
                >
                    <div className="px-2 pt-2 pb-3 space-y-1">
                        <Link to="/"
                              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                              onClick={handleLinkClick}>
                            Home
                        </Link>
                        <Link to="/pricing"
                              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                              onClick={handleLinkClick}>
                            Pricing and Services
                        </Link>
                        <Link to="/zones-info-dubai"
                              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                              onClick={handleLinkClick}>
                            Freezones on Dubai
                        </Link>
                        <Link to="/services"
                              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                              onClick={handleLinkClick}>
                            Services
                        </Link>
                        <Link to="/dubai-work-zones"
                              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                              onClick={handleLinkClick}>
                            Work Zones
                        </Link>
                    </div>
                </div>
            </div>
            {/*{isScrolled ? (*/}
            {/*    <div className="h-[80px] flex justify-center items-center">*/}
            {/*        <h2 className="text-3xl md:text-4xl text-black font-bold">SALDO PENDIENTE WEB</h2>*/}
            {/*    </div>*/}
            {/*): (*/}
            {/*    <div className="h-[80px] flex justify-center items-center">*/}
            {/*        <h2 className="text-3xl md:text-4xl text-white font-bold">SALDO PENDIENTE WEB</h2>*/}
            {/*    </div>*/}
            {/*)}*/}
        </nav>
    );
};

export default Navbar;